import { type TicketData } from '@/api/registration';
import BaseTextList from '@/base/Text/List';
import BaseTextListItem from '@/base/Text/ListItem';
import {
  UiHStack,
  UiIconCurrencyCircleDollar,
  UiStack,
  UiTag,
  UiText,
  uiStyles,
  UiButton,
  UiIconPlusCircle,
  UiIconPlus,
  UiIconShoppingCart,
  UiIconTicket,
  UiIconXCircle,
  UiIconProhibit,
  UiIconCopy, UiIconPencilSimple, UiIconCheck, UiIconCheckCircle,
} from '@/lib/ui';
import { useEventQuery } from '@/registration/hook/useEventQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import {
  useDisclosure
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useMemo, type FC } from 'react';
import TicketDrawer from './TicketDrawer';
import TimeTag from './TimeTag';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import BaseButtonPlainIcon from '@/base/Button/PlainIcon';
import { useInStockItem } from '@/registration/hook/useInStockItem';
import { useShoppingCart } from '@/registration/hook/useShoppingCart';
import BaseButtonIconText from '@/base/Button/IconText';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(duration);
export interface TicketProps {
  ticket: TicketData
  isUpsell?: boolean
  isAdded?: boolean
}

const Ticket: FC<TicketProps> = ({
  ticket,
  isUpsell,
  isAdded = false,
}) => {
  const { eventId } = useRegisterRoute();
  const { isOpen, onToggle } = useDisclosure();
  const registrationQuery = useRegistrationQuery(eventId);
  const registration = registrationQuery?.data;
  const validToDay = useMemo(() => { return dayjs(ticket.validTo); }
    , [ticket.validTo]);
  const isExpired = useMemo(() => { return validToDay.isBefore(dayjs()); }, [validToDay]);
  const displayCapacities = !ticket.hideRemainingStock;
  const displayRemainingTime = !ticket.hideRemainingTime;

  const discountStatus = useMemo(() => {
    if (!registration?.discount || !ticket?.ticketDiscounts) return undefined;
    return (ticket?.ticketDiscounts.find((ticketDiscount) => { return ticketDiscount.active && Number(ticketDiscount.id) === registration.discount?.id; }))
      ? 'applied' : 'unavailable';
  }, [ticket?.ticketDiscounts, registration?.discount]);

  const taxStatus = ticket.displayTaxInclusivePrices.isEnabled ? 'inc' : 'ex';
  const taxNames = ticket.displayTaxInclusivePrices.taxName || 'GST';
  const priceLabel = `${ticket?.priceFormat} (${taxStatus}. ${taxNames})`;
  const discountCode = registration?.discount?.discountCode;

  const inStockItem = useInStockItem(ticket);

  const isUnavailable = useMemo(
    () => {
      return isExpired || ticket.currentStock <= 0 || !inStockItem;
    },
    [isExpired, ticket.currentStock, inStockItem]
  );

  const isAlreadyPurchased = useMemo(() => {
    if (!isUpsell || !registration?.otherTickets) {
      return false;
    }
    return registration.otherTickets.some(
      (otherTicket) => { return otherTicket.id === ticket.id; }
    );
  }, [isUpsell, registration?.otherTickets, ticket.id]);

  return (
    <>
      <UiStack
        bgColor={'#fff'}
        borderRadius={uiStyles.borderRadius}
        // onClick={isExpired || ticket.currentStock <= 0 ? undefined : onToggle}
        // enableDivider={true}
        spacing={6}
        opacity={isUnavailable ? 0.6 : 1}
        px={{ base: 6, lg: 8 }}
        py={6}
        shadow={uiStyles.cardShadow}
        // borderColor={isInCart ? 'green.500' : 'transparent'}
        // borderWidth={'1px'}
        // borderStyle={'solid'}
      >
        <UiStack spacing={4}>
          <UiStack>
            <UiHStack>
              <UiHStack>
                {isUnavailable ? (
                  <UiIconProhibit />
                ) : (
                  <UiIconTicket />
                )}
              </UiHStack>
              <UiText variant='title'>{ticket?.name}</UiText>
            </UiHStack>
            <UiText variant='body2' color={'text.secondary'}>{ticket?.description}</UiText>
          </UiStack>
          <UiHStack>
            {displayRemainingTime && <TimeTag toDate={validToDay} />}
            {(ticket.currentStock <= 0 || !inStockItem) && (<UiTag p={2} colorScheme="gray" size={'sm'}>Out of stock</UiTag>)}
            {discountStatus === 'applied' && <UiTag p={2} colorScheme="green" size={'sm'}>{`${discountCode} applied with ${ticket?.discountValueFormat} discount.`}</UiTag>}
            {discountStatus === 'unavailable' && <UiTag p={2} colorScheme="gray" size={'sm'}>Discount unavailable</UiTag>}
            {isAlreadyPurchased && <UiTag p={2} colorScheme="gray" size={'sm'}>Already purchased</UiTag>}
          </UiHStack>
        </UiStack>
        <UiHStack justifyContent={'space-between'}>
          <UiHStack>
            <UiIconCurrencyCircleDollar size={'2xl'} color={'text.primary'} />
            <UiText >Price</UiText>
          </UiHStack>
          <UiText>{priceLabel}</UiText>
        </UiHStack>
        {!isUnavailable && (
          <UiStack spacing={6} alignItems={'stretch'} flexGrow={1} justifyContent={'space-between'}>
            <BaseDividerHorizontal height={0} />
            <UiHStack
              justifyContent={'flex-end'}
              // spacing={}
              // onClick={onToggle}
            >
              {/*<BaseButtonPlainIcon*/}
              {/*  label={'Add to cart'}*/}
              {/*  color={'primary.500'}*/}
              {/*  Icon={UiIconPlusCircle}*/}
              {/*  iconSize={'3xl'}*/}
              {/*/>*/}
              {isAdded ? (
                // <BaseButtonIconText
                //   Icon={UiIconCheck}
                //   onClick={onToggle}
                //   color={'green.500'}
                // >
                //   Added
                // </BaseButtonIconText>
                <UiButton
                  onClick={onToggle}
                  leftIcon={<UiIconCheck size={'xl'} color={'#fff'}/>}
                  px={4}
                  colorScheme={'green'}
                  size={'md'}
                >
                  Added
                </UiButton>
              ) : (
                // <BaseButtonIconText
                //   Icon={UiIconPlus}
                //   onClick={onToggle}
                // >
                //   Add
                // </BaseButtonIconText>
                <UiButton
                  variant={'outline'}
                  onClick={onToggle}
                  leftIcon={<UiIconPlus size={'xl'} color={'primary.500'}/>}
                  px={4}
                  size={'md'}
                >
                  Add
                </UiButton>
              )}
            </UiHStack>
          </UiStack>
        )}
      </UiStack>
      <TicketDrawer
        ticket={ticket}
        price={ticket?.priceFormat}
        isOpen={isOpen}
        onClose={onToggle}
        isAlreadyPurchased={isAlreadyPurchased}
        displayCapacities={displayCapacities}
      />
    </>
  );
};

export default Ticket;
